import { AuthOnFile } from "../../../../data-model/medicalPaOrdersModel";
import { ModalNamesForBigQuery } from "../../../../enums/eventsData";
import { getAllJCodesFromAuthOnFile } from "../../../../pages/singleOrderLayout/utils/existingDocsToLs";

/** Shared order and user context for clinical data table analytics events. */
export interface ClinicalDataAnalyticsContext {
  screenName: string;
  orgId: string;
  patientId: string;
  orderId: string;
  userId: string;
  userEmail: string;
  payerName: string;
  jcodeIcd: unknown;
  authOnFile: AuthOnFile | undefined;
}

/**
 * Builds the `additional_data` payload for the primary ICD saved event.
 *
 * @param context - Shared analytics context for the modal.
 * @param params - Save-specific ICD comparison fields.
 * @return Additional data object for BigQuery event logging.
 */
export const buildPrimaryIcdSavedAdditionalData = (
  context: ClinicalDataAnalyticsContext,
  params: {
    defaultPrimaryCode: string;
    newPrimaryIcdCode: string;
    regimenIcdState: string;
    primaryIcdCodesCount: number;
    icdCodesChanged: boolean;
  },
) => ({
  screen_name: context.screenName,
  modal_name: ModalNamesForBigQuery.CLINICAL_DATA_TABLE,
  payer_name: context.payerName,
  old_primary_icd_code: params.defaultPrimaryCode,
  new_primary_icd_code: params.newPrimaryIcdCode,
  jcode_icd: context.jcodeIcd,
  j_code: (getAllJCodesFromAuthOnFile(context.authOnFile) ?? [])
    .map((jCode) => jCode.j_code)
    .join(","),
  regimen_icd_state: params.regimenIcdState,
  count_of_icd_codes: params.primaryIcdCodesCount,
  icd_codes_changed: params.icdCodesChanged,
});

/**
 * Builds the `additional_data` payload for the Dx dropdown selection event.
 *
 * @param context - Shared analytics context for the modal.
 * @param params - Dropdown selection fields.
 * @return Additional data object for BigQuery event logging.
 */
export const buildDxCodeDropdownSelectedAdditionalData = (
  context: ClinicalDataAnalyticsContext,
  params: {
    selectedDxCode: string;
    assignedPrimaryIcdCodes: string[];
  },
) => ({
  screen_name: context.screenName,
  modal_name: ModalNamesForBigQuery.CLINICAL_DATA_TABLE,
  selected_dx_code: params.selectedDxCode,
  assigned_primary_icd_code: params.assignedPrimaryIcdCodes,
  jcode_icd: context.jcodeIcd,
  j_code: (getAllJCodesFromAuthOnFile(context.authOnFile) ?? [])
    .map((jCode) => jCode.j_code)
    .join(","),
  payer_name: context.payerName,
});
